<template src="./template.html"></template>

<script>
import olMapMixin from '@/mixin/olMapMixin'
import olMapDragBoxMixin from '@/mixin/olMapDragBoxMixin'
import roadInfoMixin from '@/mixin/roadInfoMixin'
import * as EVT from '@/consts/eventNames'
import ExtremeMapKilopostLayerManager from '@/lib/ExtremeMapKilopostLayerManager'
import ExtremeMapJointLayerManager from '@/lib/ExtremeMapJointLayerManager'
import ExtremeMapPoleLayerManager from '@/lib/ExtremeMapPoleLayerManager'
import ExtremeMapRecLayerManager from '@/lib/ExtremeMapRecLayerManager'

export default {
  name: 'extreme-map',
  props: {
    kiloposts: {
      type: Array,
      default() {
        return []
      }
    },
    joints: {
      type: Array,
      default() {
        return []
      }
    },
    poles: {
      type: Array,
      default() {
        return []
      }
    },
    stream: {
      type: Object,
    }
  },
  data() {
    return {
      currentMapType: '',
      currentRecType: 'stopped',

      selectedRoadName: '',
      roadInfos: [],

      mapTypeInfo: {
        gmap: {
          dispName: 'Street',
          getLayerFuncName: 'getGoogleMapLayer',
        },
        bing: {
          dispName: 'Aerial',
          getLayerFuncName: 'getBingMapLayer',
        },
        toggleOrderArr: ['gmap', 'bing'],
      },

      kpLayerMgr: null,
      jointLayerMgr: null,
      poleLayerMgr: null,
      recLayerMgr: null,

      onResizeFunc: null,
    }
  },
  computed: {
    componentId() {
      return `extreme-map-${this._uid}`
    },
    olMapId() {
      return `ol-map-${this._uid}`
    },
    olPopupId() {
      return `ol-popup-${this._uid}`
    },
    olPopupContentId() {
      return `ol-popup-content-${this._uid}`
    },
    olPopupCloserId() {
      return `ol-popup-closer-${this._uid}`
    },
    currentMapTypeDisp() {
      if (!this.mapTypeInfo[this.currentMapType]) { return '' }
      return this.mapTypeInfo[this.currentMapType].dispName
    },
    currentRecTypeDisp() {
      let ret = ''
      if (this.currentRecType === 'started') {
        ret = '終'
      } else if (this.currentRecType === 'stopped') {
        ret = '始'
      }
      return ret
    },
  },
  watch: {
    kiloposts() {
      const { layer } = this.kpLayerMgr.prepareLayer(this.kiloposts)
      this.addLayer(layer)
    },
    joints() {
      const { layer, layerInfo } =
        this.jointLayerMgr.prepareLayer(this.joints)
      this.addLayer(layer, {
        interaction: {
          click: layerInfo.onJointClick,
        },
      })
      if (layerInfo.extent) {
        this.fitToExtent(layerInfo.extent, {
          maxZoom: 20,
          duration: 500,
        })
      }
    },
    poles() {
      const { layer, layerInfo } =
        this.poleLayerMgr.prepareLayer(this.poles)
      this.addLayer(layer, {
        interaction: {
          click: layerInfo.onPoleClick,
        },
      })
      if (layerInfo.extent) {
        this.fitToExtent(layerInfo.extent, {
          maxZoom: 20,
          duration: 500,
        })
      }
    },
  },
  mixins: [olMapMixin, olMapDragBoxMixin, roadInfoMixin],
  mounted() {
    this.initMapManager({
      element: `ol-map-${this._uid}`,
      center: this.convCoord({
        lat: 35.679100,
        lon: 139.756932,
      }),
      maxZoom: 25,
      minZoom: 4,
      zoom: 10,
      enablePan: true,
      enableZoomButton: true,
      enableMouseWheelZoom: true,
      enableDoubleClickZoom: true,
      enableScaleLine: true,
    })

    /*
    // kinda slow...
    {
      const layer = this.getOlGMGoogleMapLayer()
      this.currentMapType = layer.name
      this.addLayer(layer)
      this.activateOlGM()
    }
    */

    {
      const layer = this.getGoogleMapLayer()
      this.currentMapType = layer.name
      this.addLayer(layer)
    }

    {
      const { layer, interaction } = this.getDragBoxDrawLayer()
      this.addLayer(layer)
      this.addMapInteraction(interaction)
    }

    this.addDebugClickEvent()
    this.setListeners()

    this.kpLayerMgr = new ExtremeMapKilopostLayerManager()
    this.kpLayerMgr.setStream(this.stream)
    this.jointLayerMgr = new ExtremeMapJointLayerManager()
    this.jointLayerMgr.setStream(this.stream)
    this.poleLayerMgr = new ExtremeMapPoleLayerManager()
    this.poleLayerMgr.setStream(this.stream)

    this.initResizeFunc()

    this.roadInfos = this.getRoadInfos()
    this.$store.dispatch(
      'localStorage/get',
      { key: 'selectedRoadNameExtremeMap' }
    ).then(name => {
      this.selectedRoadName = name || this.roadInfos[0].name
      this.setFocusAreaFromRoadName()
    })
  },
  destroyed() {
    window.removeEventListener('resize', this.onResizeFunc)
  },
  methods: {
    setListeners() {
      this.stream.recv(EVT.SET_ROAD_NAME,
        ({ roadName }) => {
          if (!roadName) { return }
          if (this.selectedRoadName === roadName) { return }
          this.selectedRoadName = roadName
          this.setFocusAreaFromRoadName()
        }
      )
    },
    initResizeFunc() {
      // resize map on window resize
      this.onResizeFunc = () => {
        const h = window.innerHeight - 126
        const elem = document.getElementById(this.olMapId)
        elem.style.height = `${h}px`
        this.updateMapSize()
      }
      this.onResizeFunc()
      window.addEventListener('resize', this.onResizeFunc)
    },
    getNextMapType() {
      const arr = this.mapTypeInfo.toggleOrderArr
      const idx = arr.indexOf(this.currentMapType)
      const nextIdx = (idx + 1) % arr.length
      return arr[nextIdx]
    },
    getMapLayer(mapType) {
      const funcName = this.mapTypeInfo[mapType].getLayerFuncName
      return this[funcName]()
    },
    changeMapType() {
      const nextMapType = this.getNextMapType()
      this.addLayer(this.getMapLayer(nextMapType), { insertAt: 0 })
      this.removeLayer(this.currentMapType)
      this.currentMapType = nextMapType
    },
    moveMapTo(extent) {
      this.fitToExtent(extent, {
        maxZoom: 20,
        duration: 500,
      })
    },
    setFocusAreaFromRoadName() {
      const roadInfo = this.roadInfos.find(e => {
        return e.name === this.selectedRoadName
      })
      if (!roadInfo) { return }
      const coords = roadInfo.coords
      this.setDragResultRectFromCoords(coords)
      const geom = this.getDragResultRectGeometry()
      this.moveMapTo(geom.getExtent())
      // 4326にしてから投げてやる
      this.stream.send(EVT.SET_FOCUS_AREA, {
        geom: this.convGeometry(geom, {
          srcProj: 'EPSG:3857',
          destProj: 'EPSG:4326',
        })
      })
    },
    setFocusAreaFromDragBox() {
      const geom = this.getDragResultRectGeometry()
      if (!geom) { return }
      this.moveMapTo(geom.getExtent())
      // 4326にしてから投げてやる
      this.stream.send(EVT.SET_FOCUS_AREA, {
        geom: this.convGeometry(geom, {
          srcProj: 'EPSG:3857',
          destProj: 'EPSG:4326',
        })
      })
    },
    onSelectedRoadNameChange() {
      this.$store.dispatch(
        'localStorage/set',
        {
          key: 'selectedRoadNameExtremeMap',
          val: this.selectedRoadName
        }
      )
    },
    startRec() {
      this.recLayerMgr = new ExtremeMapRecLayerManager()
      const { layer, layerInfo } = this.recLayerMgr.prepareLayer()
      this.addLayer(layer, {
        interaction: {
          click: layerInfo.onClick,
        },
      })
      this.currentRecType = 'started'
    },
    stopRec() {
      this.removeLayer('rec')
      this.currentRecType = 'stopped'
      this.recLayerMgr.dumpPoints()
      this.recLayerMgr = null
    },
    startStopRec() {
      if (this.currentRecType === 'started') {
        this.stopRec()
      } else {
        this.startRec()
      }
    },
  }
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
